import axios from "axios";
import beveatApiResourceOwner from "../../../api/resource_owner_beveat_api.js";
import beveatApiStaging from "../../../api/staging_beveat_api.js";
import beveatApiSandbox from "../../../api/sandbox_beveat_api.js";
import beveatApiProduction from "../../../api/production_beveat_api.js";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Checkbox, Typography } from "@material-tailwind/react";
import { urls, commonConfigs } from "../../../../config.js";
import {
  PlatformSyscodeString,
  PLATFORM_SYSCODE,
} from "../../../constants/platform_syscode.jsx";
import { InfoCircle } from "iconsax-react";
import { getUser } from "../../../services/local_service.js";
import { doublewriteConfig } from "../../../../config.js";
import { INVOICE_PLAN_SYSCODE, InvoicePlanSyscodeString } from "../../../constants/invoice_plan_syscode.jsx";
import { DEVELOPER_PROJ_STATUS } from "../../../constants/developer_proj_status.jsx";
import { INVOICE_SYSCODE } from "../../../constants/invoice_syscode.jsx";
import { DEVELOPER_PROJ_TABLE_SYSCODE } from "../../../constants/developer_proj_table_syscode.jsx";
import { CERTIFICATE_SYSCODE, CertificateSyscodeString } from "../../../constants/certificate_syscode.jsx";

const errorIcon = (
  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
    <InfoCircle className="h-5 w-5 text-red-500" aria-hidden="true" />
  </div>
);

const CreateProject = () => {
  const location = useLocation();
  const presetOptions = location.state?.presetOptions;
  const presetEnvironment = location.state?.presetEnvironment;
  const isProd = location.state?.isProd ? location.state?.isProd : (presetOptions.invoicePlanSyscode === INVOICE_PLAN_SYSCODE.Production ? 1 : 0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [productionPlans, setProductionPlans] = useState();
  const [isCodeApplied, setIsCodeApplied] = useState(false);
  const [isVoucherValid, setIsVoucherValid] = useState(false);
  const [optionLoad, setOptionLoad] = useState(true);
  const [projects, setProjectsData] = useState([]);
  const [developerProjName, setDeveloperProjName] = useState(
    presetOptions?.developerProjName || ""
  );
  const [developerProjs, setDeveloperProjs] = useState([]);
  const [developerProjsData, setDeveloperProjsData] = useState([]);
  const [industryApiOptions, setIndustryApiOptions] = useState([]);
  const [organisationApiOptions, setOrganisationApiOptions] = useState([]);
  const [load, setLoad] = useState(false);
  const accountId = getUser()?.account_id;
  const navigate = useNavigate();
  const ssoId = getUser()?.sso_id;
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    control,
    watch
  } = useForm({
    defaultValues: {
      environment: presetOptions?.invoicePlanSyscode || presetEnvironment,
      subscription_plan: presetOptions?.subscriptionId || null,
      industryAccess: presetOptions?.industryApiOptions || [],
      organisationAccess: presetOptions.organisationApiOptions ?
        presetOptions?.organisationApiOptions.filter(
          (project) => !(project.label === "beveatshop")
        ) || [] : [],
      platform_syscode: presetOptions?.platformSyscode,
      developer_proj_website_url: presetOptions?.websiteUrl,
    },
  });
  const getProjectsUrl =
    urls.resourceOwnerUrl + commonConfigs.apiUrls.getProjects();
  const getProductionPricePlansUrl =
    urls.resourceOwnerUrl + commonConfigs.apiUrls.getProductionPlans();
  const devProjTableSyscode = presetOptions.invoicePlanSyscode;
  // Get the certificate_syscode value from form
  const certificateSyscode = watch("certificate_syscode");
  // Determine if a field should be disabled when certificate syscode is not selected
  const shouldDisable = presetOptions.invoicePlanSyscode === INVOICE_PLAN_SYSCODE.Production && !certificateSyscode;

  async function getValidateProjects(certSyscode) {
    if (!certSyscode) return;
    const getValidateProjectsUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.getValidateProjects(ssoId, certSyscode.value);

    await beveatApiResourceOwner
      .get(getValidateProjectsUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        // console.log(res.data.result.data);
        const projs = (res.data.result.data).map((project) => ({
          value: project.developer_proj_id,
          label: project.developer_proj_name,
        }));
        setDeveloperProjs(projs);
        setDeveloperProjsData(res.data.result.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    getValidateProjects(certificateSyscode);
  }, [certificateSyscode]);

  useEffect(() => {
    async function getProjectsData() {
      await beveatApiResourceOwner
        .get(getProjectsUrl, { headers: commonConfigs.resourceOwnerApiHeader })
        .then((res) => {
          // console.log(res.data.result.data);
          setProjectsData(res.data.result.data);
          setOptionLoad(false);
        })
        .catch((err) => {
          console.log(err);
        });
    }

    if (optionLoad) {
      getProjectsData().catch((err) => {
        console.log("err for getting projects: " + err);
      });
      getProductionPlans().catch((err) => {
        console.log("err for production plans: " + err);
      });
    }
  }, []);

  useEffect(() => {
    const matchedOption = platformTypeOptions.find(
      (option) => option.value === presetOptions?.platformSyscode
    );

    if (matchedOption) {
      setValue("platform_syscode", matchedOption);
    }
  }, [presetOptions?.platformSyscode, setValue]);

  async function validateVoucher(voucherCode) {
    const validateVoucherUrl =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.validateVoucher(
        accountId,
        voucherCode,
        presetOptions?.subscriptionId.value
      );
    await beveatApiResourceOwner
      .get(validateVoucherUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        // console.log(res.data.result.data);
        const isValid = res.data.result.data.is_valid;
        if (isValid) {
          setIsCodeApplied(true);
          setIsVoucherValid(true);
          alert("Voucher code applied successfully!");
        } else {
          alert(`${res.data.result.data.message}`);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  useEffect(() => {
    if (projects) {
      const industryApiOptions = projects
        .filter(
          (project) =>
            project.project_billing_config_id == 1 &&
            project.project_name.toLowerCase().includes("industry")
        )
        .map((project) => ({
          value: project.project_id,
          label: project.project_name,
        }));
      const organisationApiOptions = projects
        .filter(
          (project) =>
            project.project_billing_config_id == 1 &&
            !project.project_name.toLowerCase().includes("industry") &&
            project.project_name !== 'beveatshop'
        )
        .map((project) => ({
          value: project.project_id,
          label: project.project_name,
        }));
      setIndustryApiOptions(industryApiOptions);
      setOrganisationApiOptions(organisationApiOptions);
    }
  }, [projects]);

  const environmentOptions = [
    { value: "0", label: "Staging" },
    { value: "1", label: "Production" },
  ];
  function environmentsOptions(invoicePlanSyscode) {
    const value = InvoicePlanSyscodeString[invoicePlanSyscode];
    return value ? [{ value: invoicePlanSyscode, label: value }] : []
  }

  const options_plan = [
    { value: null, label: "None" },
    { value: "1", label: "IHL-NETS-Fintech Full Stack Developer Course" },
  ];

  const certSyscodeMockup = [
    { value: CERTIFICATE_SYSCODE.UAT_NETS_QR_Web, label: CertificateSyscodeString[CERTIFICATE_SYSCODE.UAT_NETS_QR_Web], isDisabled: false },
    { value: CERTIFICATE_SYSCODE.UAT_NETS_Click, label: CertificateSyscodeString[CERTIFICATE_SYSCODE.UAT_NETS_Click], isDisabled: true },
    { value: CERTIFICATE_SYSCODE.UAT_eNETS_Web, label: CertificateSyscodeString[CERTIFICATE_SYSCODE.UAT_eNETS_Web], isDisabled: true },
  ]

  const platformTypeOptions = Object.entries(PLATFORM_SYSCODE).map(
    ([key, value]) => ({
      value: value,
      label: PlatformSyscodeString[value],
    })
  );

  const handleApplyVoucher = (e) => {
    e.preventDefault();
    if (!voucherCode) {
      alert("Please enter a voucher code.");
      return;
    }
    validateVoucher(voucherCode);
  };

  const handleRemoveVoucher = (e) => {
    e.preventDefault();
    setVoucherCode("");
    setIsCodeApplied(false);
  };

  const handleCancel = (event) => {
    event.preventDefault(); // Prevent default anchor behavior
    navigate(-1);
  };

  async function getProductionPlans() {
    await beveatApiResourceOwner
      .get(getProductionPricePlansUrl, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then((res) => {
        // console.log(res.data.result.data);
        setProductionPlans(res.data.result.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  function mapAccess(items, subscriptionId, plans = null) {
    return items.map((item) => {
      const plan = plans
        ? plans.find((prodPlan) => prodPlan.project_id === item.value)
        : null;
      return {
        project_id: item.value,
        org_invoice_plan_id: plan ? plan.org_invoice_plan_id : subscriptionId,
      };
    });
  }

  function addPresetProjects(presetOptions, subscriptionId) {
    return [
      ...presetOptions.industryApiOptions,
      ...presetOptions.organisationApiOptions,
    ]
      .filter((project) => project.label === "beveatshop")
      .map((project) => ({
        project_id: project.value,
        org_invoice_plan_id: subscriptionId,
      }));
  }

  function formatData(data) {
    var developerSsoId = localStorage.getItem("ssoId");
    var subscriptionId = data?.subscription_plan?.value;
    let invoicePlanSyscode = presetOptions.invoicePlanSyscode;
    if (presetOptions?.developerProjId) {
      let updateData = {
        developer_proj_name: data.developer_proj_name,
        developer_sso_id: developerSsoId,
        developer_proj_website_url: data.developer_proj_website_url,
        access: null,
        platform_syscode: data.platform_syscode.value,
        invoice_plan_syscode: invoicePlanSyscode,
        org_invoice_plan_id: subscriptionId,
        voucher_code: voucherCode,
        invoice_plan: presetOptions.invoicePlan,
        invoice_syscode: presetOptions.invoiceSyscode,
        invoice_rate_syscode: presetOptions.invoiceRateSyscode
      };
      // console.log("formatdata", updateData);
      return updateData;
    } else {
      var newData = data;
      var platformSyscode = data.platform_syscode.value;

      let access = [];
      if (data.industryAccess) {
        access = mapAccess(
          data.industryAccess,
          subscriptionId,
          isProd ? productionPlans : null
        );
      }
      if (data.organisationAccess) {
        const organisationAccess = mapAccess(
          data.organisationAccess,
          subscriptionId,
          isProd ? productionPlans : null
        );
        access = [...access, ...organisationAccess];
      }
      if (!isProd) {
        access = [
          ...access,
          ...addPresetProjects(presetOptions, subscriptionId),
        ];
        newData.org_invoice_plan_id = subscriptionId;
      } else {
        const targetApiOption = projects.find(
          (option) => option.project_name === "beveatshop"
        );
        if (targetApiOption) {
          access.push({
            project_id: targetApiOption.project_id,
            org_invoice_plan_id: null,
          });
        }
      }
      newData.invoice_plan_syscode = invoicePlanSyscode;
      newData.invoice_plan = presetOptions.invoicePlan;
      newData.invoice_syscode = presetOptions.invoiceSyscode;
      newData.invoice_rate_syscode = presetOptions.invoiceRateSyscode;
      newData.developer_sso_id = developerSsoId;
      newData.developer_proj_name = developerProjName ? developerProjName : data.developer_proj_name.label;
      newData.platform_syscode = platformSyscode;
      newData.access = access;
      newData.voucher_code = voucherCode;
      newData.org_invoice_plan_id = subscriptionId;
      console.log("formatdata", newData);
      return newData;
    }
  }

  async function doublewriteProjects(body, devProj) {
    const { url, headers } = doublewriteConfig(process.env.REACT_APP_ENV);
    var doublewriteData = body;

    doublewriteData.developer_proj_id = devProj.developer_proj_id;
    doublewriteData.developer_subscription_id =
      devProj.developer_subscription_id;
    doublewriteData.signature_scrt_key = devProj.developer_proj_scrt_key;
    doublewriteData.developer_proj_key_value = devProj.developer_proj_key_value;
    doublewriteData.access = devProj.access;

    const doublewriteUrl =
      url + commonConfigs.apiUrls.doublewriteDeveloperProject(devProjTableSyscode);
    try {
      let apiInstance;

      if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_STAGING_URL) {
        apiInstance = beveatApiStaging;
      } else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_SANDBOX_URL) {
        apiInstance = beveatApiSandbox;
      } else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_PRODUCTION_URL) {
        apiInstance = beveatApiProduction;
      } else {
        apiInstance = axios;
      }

      await apiInstance.post(doublewriteUrl, doublewriteData, { headers: headers });
    } catch (error) {
      console.error("Error doublewriting project:", error);
    }
  }

  async function createInvoice(data, devProj) {
    const createInvoiceUrl =
      urls.resourceOwnerUrl + commonConfigs.apiUrls.createInvoice();
    let body;
    if (data.voucher_code) {
      // console.log("Fetching voucher:", data.voucher_code);
      const getVoucherUrl =
        urls.resourceOwnerUrl +
        commonConfigs.apiUrls.getVoucher(data.voucher_code);
      await beveatApiResourceOwner
        .get(getVoucherUrl, {
          headers: commonConfigs.resourceOwnerApiHeader,
        })
        .then((res) => {
          try {
            // console.log("Voucher fetched:", res.data.result.data);
            body = {
              developer_proj_id: devProj.developer_proj_id,
              org_invoice_plan_id: data.org_invoice_plan_id,
              invoice_plan_syscode: data.invoice_plan_syscode,
              invoice_rate_syscode: data.invoice_rate_syscode,
              invoice_syscode: data.invoice_syscode,
              invoice_plan: data.invoice_plan,
              account_id: accountId,
              discount_type: 0,
              discount_percent: 0,
              discount_amt: 0,
              voucher_syscode: res.data.result.data.voucher_syscode,
              voucher_code: data.voucher_code,
              voucher_amt: res.data.result.data.voucher_value,
              org_id: process.env.REACT_APP_RESOURCE_OPENAPIPAAS_ORG_ID,
            };
          } catch (err) {
            console.log(err);
          }
        })
        .catch((err) => {
          // console.error("❌ Error fetching voucher:", err);
          setIsDisabled(false); // Enable the submit button if there's an error
          throw err;
        });
    } else {
      body = {
        developer_proj_id: devProj.developer_proj_id,
        org_invoice_plan_id: data.org_invoice_plan_id,
        invoice_plan_syscode: data.invoice_plan_syscode,
        invoice_rate_syscode: data.invoice_rate_syscode,
        invoice_syscode: data.invoice_syscode,
        invoice_plan: data.invoice_plan,
        account_id: accountId,
        discount_type: 0,
        discount_percent: 0,
        discount_amt: 0,
        org_id: process.env.REACT_APP_RESOURCE_OPENAPIPAAS_ORG_ID,
      };
    }
    // console.log("Sending invoice creation payload:", body);
    // console.log("🚀 Final payload to /createInvoice:", JSON.stringify(body, null, 2));
    const response = await beveatApiResourceOwner
      .post(createInvoiceUrl, body, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .catch((err) => {
        // console.error("❌ Error creating invoice:");

        // if (err.response) {
        //   console.error("Status:", err.response.status);
        //   console.error("Response Data:", err.response.data);

        //   alert(
        //     `Invoice creation failed: ${JSON.stringify(err.response.data.result?.message || err.response.data)
        //     }`
        //   );
        // } else {
        //   console.error("General Error:", err.message);
        //   alert(`Invoice creation failed: ${err.message}`);
        // }

        setIsDisabled(false);
        throw err;
      });
    // console.log("Invoice response:", response.data.result.data);
    return response.data.result.data;
  }

  const onSubmit = async (data) => {
    setIsDisabled(true); // Disable the submit button

    // Add URL validation here
    if (data.developer_proj_website_url && data.developer_proj_website_url.trim() !== '') {
      const urlPattern = /^https?:\/\/.+/i;
      if (!urlPattern.test(data.developer_proj_website_url)) {
        setIsDisabled(false);
        alert("Please include http:// or https:// in your website URL.");
        return;
      }
    }

    if (voucherCode && !isCodeApplied) {
      // Check if a voucher code is typed but not applied
      setIsDisabled(false);
      alert("Please apply the voucher code or clear the input field.");
      return;
    }

    if (voucherCode && !isVoucherValid) {
      // Display an error if the voucher code is invalid
      setIsDisabled(false);
      alert("Voucher applied is invalid.");
      return;
    }

    if (voucherCode === '') {
      // If not in production and no voucher code is provided
      // Display an alert and do not proceed with the submission
      setIsDisabled(false);
      alert("Coming soon. In the meantime, you can obtain a voucher code from the instructor.");
      return;
    }

    const newData = formatData(data);

    console.log("newData", newData);

    if (!isProd) {
      if (presetOptions?.developerProjId) {
        let updateProjectUrl = `${urls.resourceOwnerUrl
          }${commonConfigs.apiUrls.UpdateDeveloperProject(
            presetOptions?.developerProjId, devProjTableSyscode
          )}`;
        beveatApiResourceOwner
          .put(updateProjectUrl, newData, {
            headers: commonConfigs.resourceOwnerApiHeader,
          })
          .then(async (res) => {
            const result = await createInvoice(newData, res.data.result.data);
            await updateDoublewriteProject(
              newData,
              presetOptions?.developerProjId,
            );
            setIsDisabled(false); // Enable the submit button after API call
            if (result.invoice_total_nett_amt == 0) {
              await createDeveloperCourse(
                res.data.result.data.developer_proj_id,
                newData.org_invoice_plan_id
              );
            } else {
              localStorage.setItem("txnId", result.txn_id);
              localStorage.setItem("amount", result.invoice_total_nett_amt);
              localStorage.setItem(
                "projId",
                res.data.result.data.developer_proj_id
              );
              localStorage.setItem("planId", newData.org_invoice_plan_id);
              window.location.href = "/payments";
            }
          })
          .catch((err) => {
            const err_msg = JSON.stringify(err.response.data.result.message);
            alert(`Error: ${err_msg}`);
            setIsDisabled(false); // Enable the submit button if there's an error
          });
      } else {
        const createProjectUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.CreateDeveloperProject(DEVELOPER_PROJ_TABLE_SYSCODE.STAGING);
        beveatApiResourceOwner
          .post(createProjectUrl, newData, {
            headers: commonConfigs.resourceOwnerApiHeader,
          })
          .then(async (res) => {
            const result = await createInvoice(newData, res.data.result.data);
            await doublewriteProjects(newData, res.data.result.data, DEVELOPER_PROJ_TABLE_SYSCODE.STAGING);
            setIsDisabled(false); // Enable the submit button after API call
            if (result.invoice_total_nett_amt === 0) {
              await createDeveloperCourse(
                res.data.result.data.developer_proj_id,
                newData.org_invoice_plan_id
              );
            } else {
              localStorage.setItem("txnId", result.txn_id);
              localStorage.setItem("amount", result.invoice_total_nett_amt);
              localStorage.setItem(
                "projId",
                res.data.result.data.developer_proj_id
              );
              localStorage.setItem("planId", newData.org_invoice_plan_id);
              window.location.href = "/payments";
            }
          })
          .catch((err) => {
            alert(`Error: ${err}`);
            setIsDisabled(false); // Enable the submit button if there's an error
          });
      }
    } else {
      console.log("Prod creation");

      const createProjectUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.CreateDeveloperProject(DEVELOPER_PROJ_TABLE_SYSCODE.PRODUCTION);
      // console.log("Create Project URL:", createProjectUrl);
      // console.log("Payload (newData):", newData);
      beveatApiResourceOwner
        .post(createProjectUrl, newData, {
          headers: commonConfigs.resourceOwnerApiHeader,
        })
        .then(async (res) => {
          // console.log("Project created:", res.data.result.data);
          const result = await createInvoice(newData, res.data.result.data);
          setIsDisabled(false); // Enable the submit button after API call
          // console.log("Invoice created, result", result);
          if (result.invoice_total_nett_amt === 0) {
            // console.log("Zero invoice amount. Creating course...");
            await createDeveloperCourse(
              res.data.result.data.developer_proj_id,
              newData.org_invoice_plan_id
            );
            // console.log("Redirecting to /projects");
            window.location.href = "/projects";
          } else {
            localStorage.setItem("txnId", result.txn_id);
            localStorage.setItem("amount", result.invoice_total_nett_amt);
            localStorage.setItem(
              "projId",
              res.data.result.data.developer_proj_id
            );
            localStorage.setItem("planId", newData.org_invoice_plan_id);
            // console.log("Redirecting to /payments");
            window.location.href = "/payments";
          }
          // window.location.href = "/projects";
        })
        .catch((err) => {
          // console.error("Error during project creation:", err);
          alert(`Error: ${err}`);
          setIsDisabled(false); // Enable the submit button if there's an error
        });
    }
  };
  async function createDeveloperCourse(projId, invoicePlanId) {
    const createDeveloperCourseUrl =
      urls.resourceOwnerUrl +
      commonConfigs.apiUrls.createDeveloperCourse(accountId);

    const body = {
      developer_proj_id: projId,
      org_invoice_plan_id: invoicePlanId,
    };
    await beveatApiResourceOwner
      .post(createDeveloperCourseUrl, body, {
        headers: commonConfigs.resourceOwnerApiHeader,
      })
      .then(async (res) => {
        // console.log(res.data.result.data);
        const body = {
          developer_proj_status: DEVELOPER_PROJ_STATUS.Active,
          developer_proj_expiry: res.data.result.data.course_expiry,
        };
        await updateDoublewriteProject(body, projId);
        window.location.href = "/projects";
      })
      .catch((err) => {
        console.log(err);
        throw err;
      });
  }
  async function updateDoublewriteProject(body, projId) {
    const { url, headers } = doublewriteConfig(process.env.REACT_APP_ENV);
    const doublewriteUrl =
      url + commonConfigs.apiUrls.UpdateDeveloperProject(projId, devProjTableSyscode);
    try {
      let apiInstance;

      if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_STAGING_URL) {
        apiInstance = beveatApiStaging;
      } else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_SANDBOX_URL) {
        apiInstance = beveatApiSandbox;
      } else if (url === process.env.REACT_APP_BASE_OPENAPIPAAS_PRODUCTION_URL) {
        apiInstance = beveatApiProduction;
      } else {
        apiInstance = axios;
      }

      await apiInstance.put(doublewriteUrl, body, { headers: headers });
    } catch (error) {
      console.error("Error updating doublewrite project:", error);
    }
  }

  if (!load) {
    return (
      <div>
        <div
          className="mt-16 desktop:space-x-8  mobile:pt-2  mobile:pb-4 mobile:space-x-2"
          style={{ textAlign: "-webkit-center", marginTop: "-2px" }}
        >
          <div
            className="bg-white border-white border rounded-2xl m-5 p-4"
            style={{
              height: "inherit",
              width: "65%",
              minHeight: 0,
              minWidth: 0,
            }}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              {presetOptions.invoicePlanSyscode === INVOICE_PLAN_SYSCODE.Production &&
                <div className="mt-2">
                  <label
                    htmlFor="developer_proj_name"
                    className="block text-sm font-medium text-gray-600 mt-8"
                    style={{ textAlign: "left", marginBottom: "5px" }}
                  >
                    Certificate Type
                  </label>
                  <div
                    className="relative rounded-md shadow-sm"
                    style={{ color: "black" }}
                  >
                    <div className="sm:col-span-2" style={{ color: "black" }}>
                      <Controller
                        name="certificate_syscode"
                        control={control}
                        // rules={{ required: true }}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            options={certSyscodeMockup}
                            isSearchable={true}
                            placeholder="Please Select a Certificate Type first!"
                            className="text-sm"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              }

              <div className="mt-2">
                <div className="mt-2">
                  <label
                    htmlFor="developer_proj_name"
                    className="block text-sm font-medium text-gray-600 mt-8"
                    style={{ textAlign: "left", marginBottom: "5px" }}
                  >
                    Project Name
                  </label>
                  <div
                    className="relative rounded-md shadow-sm"
                    style={{ color: "black" }}
                  >
                    {presetOptions.invoicePlanSyscode === INVOICE_PLAN_SYSCODE.Production ?
                      <div className="sm:col-span-2" style={{ color: "black" }}>
                        <Controller
                          name="developer_proj_name"
                          control={control}
                          // rules={{ required: true }}
                          render={({ field }) => (
                            <Select
                              isClearable
                              {...field}
                              options={developerProjs}
                              isSearchable={true}
                              placeholder="Select Project"
                              className="text-sm"
                              isDisabled={shouldDisable}
                              onChange={(selectedOption) => {
                                field.onChange(selectedOption);
                                if (selectedOption) {
                                  const selectedProj = developerProjsData.find(item => item.developer_proj_id === selectedOption.value);
                                  if (selectedProj) {
                                    const platformOption = platformTypeOptions.find(item => item.value === selectedProj.platform_syscode);
                                    setValue("platform_syscode", platformOption);
                                    setValue("developer_proj_website_url", selectedProj.developer_proj_website_url);
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </div>
                      :
                      <input
                        type="text"
                        name="developer_proj_name"
                        id="developer_proj_name"
                        className={
                          !errors.developer_proj_name
                            ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                            : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                        }
                        placeholder="Enter Name of Project"
                        {...register("developer_proj_name", { required: true })}
                        value={developerProjName}
                        onChange={(e) => setDeveloperProjName(e.target.value)}
                      />
                    }
                    {errors.developer_proj_name?.type === "required" &&
                      errorIcon}
                  </div>
                </div>
              </div>
              <label
                htmlFor="platform_syscode"
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Platform Type
              </label>
              <div className="sm:col-span-2" style={{ color: "black" }}>
                <Controller
                  name="platform_syscode"
                  control={control}
                  // rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      options={platformTypeOptions}
                      isSearchable={true}
                      placeholder="Select Platform Type"
                      className="text-sm"
                      isDisabled={shouldDisable}
                    />
                  )}
                />
              </div>
              <label
                htmlFor="environment"
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Project Environment
              </label>
              <div className="sm:col-span-2" style={{ color: "black" }}>
                <Controller
                  name="environment"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      value={environmentsOptions(presetOptions?.invoicePlanSyscode)}
                      isSearchable={true}
                      isDisabled={!!presetOptions || !!presetEnvironment}
                      placeholder="Select Environment"
                      className="text-sm"
                    // onChange={(selectedOption) => {
                    //   field.onChange(selectedOption);
                    //   // console.log(selectedOption);
                    //   if (selectedOption) {
                    //     setEnvironment(selectedOption.value);
                    //   }
                    // }}
                    />
                  )}
                />
              </div>
              <label
                htmlFor="developer_proj_access"
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Industry API(s)
              </label>
              <div className="sm:col-span-2" style={{ color: "black" }}>
                <Controller
                  name="industryAccess"
                  control={control}
                  // rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      options={industryApiOptions}
                      isDisabled={!!presetOptions?.industryApiOptions}
                      isSearchable={true}
                      isMulti={true}
                      placeholder="Select API"
                      className="text-sm"
                    />
                  )}
                />
              </div>
              <label
                htmlFor="developer_proj_access"
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Organisation API(s)
              </label>
              <div className="sm:col-span-2" style={{ color: "black" }}>
                <Controller
                  name="organisationAccess"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      options={organisationApiOptions}
                      isDisabled={!!presetOptions?.organisationApiOptions}
                      isSearchable={true}
                      isMulti={true}
                      placeholder="Select API"
                      className="text-sm"
                    />
                  )}
                />
              </div>
              <label
                htmlFor="developer_proj_website_url"
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Project Website URL
              </label>
              <div className="relative rounded-md shadow-sm mb-2 text-black">
                <input
                  type="text"
                  name="developer_proj_website_url"
                  id="developer_proj_website_url"
                  className={
                    !errors.developer_proj_website_url
                      ? `block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md ${shouldDisable ? "bg-[#f5f5f5] text-gray-500 cursor-not-allowed" : "bg-white"}`
                      : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                  }
                  placeholder="Enter Project Website URL"
                  {...register("developer_proj_website_url")}
                  style={{ marginBottom: "20px" }}
                  disabled={shouldDisable}
                />
                {errors.developer_proj_website_url?.type === "required" &&
                  errorIcon}
              </div>

              <div
                className="block text-sm font-medium text-gray-600 mt-4"
                style={{ textAlign: "left", marginBottom: "5px" }}
              >
                Subscription Plan
              </div>

              <div
                className="mt-1 sm:col-span-2 mb-4"
                style={{ color: "black" }}
              >
                <Controller
                  name="subscription_plan"
                  control={control}
                  // rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      isClearable
                      {...field}
                      // options={options_plan}
                      isDisabled={!!presetOptions?.subscriptionId}
                      isSearchable={true}
                      placeholder="Select Subscription Plan"
                      className="text-sm"
                    />
                  )}
                />
              </div>
              <div
                className="mt-1 sm:col-span-2 mb-8"
                style={{ color: "black" }}
              >
                <label
                  htmlFor="voucher_code"
                  className="block text-sm font-medium text-gray-600"
                  style={{ textAlign: "left", marginBottom: "5px" }}
                >
                  Voucher Code:
                </label>
                <div className="relative text-black">
                  <div className="flex items-center">
                    <input
                      type="text"
                      name="voucher_code"
                      id="voucher_code"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value)}
                      className={
                        !errors.voucher_code
                          ? "block w-full pr-10 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md bg-white"
                          : "block w-full pr-10 border-red-300 text-red-900 placeholder-red-900 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
                      }
                      placeholder="Enter Voucher Code"
                      disabled={isCodeApplied}
                    />
                    {isCodeApplied ? (
                      <button
                        onClick={handleRemoveVoucher}
                        className="ml-2 w-44 px-4 py-2 hover:bg-red-500 text-red-500 hover:text-white outline outline-red-500 rounded-md"
                      >
                        Remove Voucher
                      </button>
                    ) : (
                      <button
                        onClick={handleApplyVoucher}
                        className="ml-2 w-44 px-4 py-2 hover:bg-blue-500 text-blue-500 hover:text-white outline outline-blue-500 rounded-md"
                      >
                        Apply Voucher
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* )} */}

              {/* checkbox */}
              {/* <div style={{ textAlign: "left", marginLeft: "-1.8%", marginTop: "-20px" }}>
                <Checkbox
                  className="border-black border-1"
                  label={
                    <div>
                      <Typography
                        variant="small"
                        color="gray"
                        className="font-normal"
                        style={{ marginLeft: "-3px" }}
                      >
                        I grant permission for this project to be made and
                        readily used
                      </Typography>
                    </div>
                  }
                  required={true}
                />
              </div> */}

              {/* create button */}
              <div>
                <Link to="#" onClick={handleCancel}>
                  <button
                    type="button"
                    className="hover:bg-red-500 text-red-500 hover:text-white outline outline-red-500 font-bold py-2 px-12 rounded-full mt-10"
                    disabled={isDisabled}
                  >
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="hover:bg-blue-500 text-blue-500 hover:text-white outline outline-blue-500 font-bold py-2 px-12 rounded-full ml-5 mt-10"
                  disabled={isDisabled}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
};

export default CreateProject;
